/**
 * src/utils/interpretations.js
 * 金融占星術・マンデン占星術に特化した解釈データベース
 * 修正版 [2026-02-23]: 構文エラー修正・定数統合済み
 * ラインホルト・エバーティン『The Combination of Stellar Influences』(COSI) 準拠
 */

// =============================================================================
// 1. アスペクト解釈データ (全66ペア)
// キーは [planet1, planet2].sort().join('-') のアルファベット順
// =============================================================================

export const ASPECT_MEANINGS = {
  // --- Chiron (カイロン) 絡み ---
  "Chiron-Jupiter": {
    keywords: ["制度の健全化", "倫理性", "教訓的な調整"],
    text: "肥大化した期待や社会システムが、より誠実な形へと正される動き。過去の過ちから学び、長期的に安定した成長基盤を固める機会を示唆します。"
  },
  "Chiron-Mars": {
    keywords: ["行動の是正", "急激な再考", "修復のエネルギー"],
    text: "衝動的な活動やエネルギー管理における弱点が露呈しやすいとき。古い傷が刺激されることで、かえって抜本的な改善へ向かう力が湧きます。"
  },
  "Chiron-Mercury": {
    keywords: ["情報の取捨選択", "システムの見直し", "慎重な判断"],
    text: "情報伝達やビジネスの意思決定において、細部の検証を迫られる局面。神経質な展開になりやすいですが、精密な分析が事態を好転させます。"
  },
  "Chiron-Moon": {
    keywords: ["情緒の癒やし", "内面の対話", "共感の再発見"],
    text: "大衆感情や投資家心理がナイーブになり、過去のショックを想起しやすい時期。不安を受け入れつつ、内面的な安定を図ることが重要になります。"
  },
  "Chiron-Neptune": {
    keywords: ["意識の明晰化", "霧の解消", "本質的な救済"],
    text: "不透明な状況下で、隠されていた課題が浮き彫りになります。幻想が捨て去られ、現実的な解決策や救済の道が見えてくる過渡期です。"
  },
  "Chiron-NorthNode": {
    keywords: ["関係の修復", "集団の浄化", "運命の交差点"],
    text: "集団の中での不和が解消され、より強い絆へと再編される転換点。過去の因縁から解放され、新たな目的を共有する人々との出会いがあります。"
  },
  "Chiron-Pluto": {
    keywords: ["抜本的な再生", "不可逆な変容", "根底からの癒やし"],
    text: "組織やシステムの深部に潜んでいた脆弱性が解消される、痛みを伴うが強力な再生。逃れられない宿命的な変化を経て、強靭な構造が築かれます。"
  },
  "Chiron-Saturn": {
    keywords: ["規律の再構築", "基盤の修復", "地道な改善"],
    text: "古い体制や制限が、現代的なニーズに合わせて再定義されるタイミング。重苦しい空気はありますが、着実な努力によって盤石な成果を目指せます。"
  },
  "Chiron-Sun": {
    keywords: ["自己の回復", "認識の刷新", "指導力の洗練"],
    text: "存在意義やリーダーシップの本質が問われ、洗練されていくプロセス。自らの弱さをエネルギーに変え、社会に対する独自の貢献を見出す時期です。"
  },
  "Chiron-Uranus": {
    keywords: ["革新への挑戦", "突破口の発見", "突発的な気付き"],
    text: "技術や手法の刷新をめぐる激震と、そこから生まれる革新。既存のやり方に執着せず、意図せぬ変化を受け入れることで新しいステージへ進みます。"
  },
  "Chiron-Venus": {
    keywords: ["価値の再定義", "情緒的な和解", "調和の回復"],
    text: "金銭感覚や対人関係における歪みが修正され、本来の心地よさを取り戻すとき。外面的な美しさよりも、内的な充足感に基づいた選択が幸運を呼びます。"
  },

  // --- Jupiter (木星) 絡み ---
  "Jupiter-Mars": {
    keywords: ["旺盛な活力", "事業の拡大", "勝利の確信"],
    text: "強い意志と実行力が、最良の成果を引き出す配置。目標に向かって果敢に挑戦することで、法的な係争や困難な交渉を有利に進めることが期待できます。"
  },
  "Jupiter-Mercury": {
    keywords: ["豊かな知識", "商才の発揮", "広範な視野"],
    text: "高い理解力と建設的な思考力。ポジティブなコミュニケーションがビジネスを成功に導き、有益な情報交換や学習において大きな進展が見込めます。"
  },
  "Jupiter-Moon": {
    keywords: ["心の豊かさ", "大衆の支持", "社会的調和"],
    text: "他者への慈しみと、周囲からの信頼。投資家心理は楽観的に傾きやすく、消費の活性化や公私の円満なバランスが社会全体を明るく照らします。"
  },
  "Jupiter-Neptune": {
    keywords: ["崇高な理想", "想像力の飛躍", "慈悲"],
    text: "果てしない想像力と高い精神性が発揮される時期。市場では夢を描く動きが強まりますが、事実の確認を怠らず、過度な陶酔には注意を払うべきです。"
  },
  "Jupiter-NorthNode": {
    keywords: ["拡大する縁", "協力の連鎖", "公益の追求"],
    text: "社会的なネットワークが広がり、意義のある協力関係が築かれます。多くの人々を巻き込むプロジェクトや、海外との提携において成功を収める好機です。"
  },
  "Jupiter-Pluto": {
    keywords: ["強大な経済力", "再生への執念", "組織の刷新"],
    text: "執念に近い情熱が、圧倒的な富や影響力を生み出します。逆境からの劇的なV字回復や、大規模な組織改革によって強固な地位を確立する配置です。"
  },
  "Jupiter-Saturn": {
    keywords: ["規律ある発展", "構造の成熟", "安定の追求"],
    text: "拡大への意欲と現実的な制限がうまく噛み合うとき。時間をかけて築き上げたものが評価され、持続可能な発展のための基盤が再構築されます。"
  },
  "Jupiter-Sun": {
    keywords: ["健全な自信", "成功の享受", "公的認知"],
    text: "生命力に満ち溢れ、社会的地位や名声が高まる幸運な期間。全体的な意思決定がスムーズに進み、主要な計画の発表や公的な承認を得るのに適しています。"
  },
  "Jupiter-Uranus": {
    keywords: ["予期せぬ突破", "刷新的な成功", "チャンスの到来"],
    text: "突発的な幸運や、これまでの常識を覆すような発見。独創的な発想が経済的な利益に直結し、停滞した状況を力強く打開する力が働きます。"
  },
  "Jupiter-Venus": {
    keywords: ["豊潤な魅力", "平和な解決", "金運の向上"],
    text: "洗練された趣味と経済的な充足感。人間関係の調和がもたらされ、交渉事は平和的に進展します。贅沢な楽しみや芸術的な評価も高まりやすいときです。"
  },

  // --- Mars (火星) 絡み ---
  "Mars-Mercury": {
    keywords: ["決断の速さ", "鋭利な思考", "議論の活発化"],
    text: "考えを即座に行動へ移す俊敏さ。議論においては鋭い指摘ができますが、性急な判断による論争には注意が必要。ビジネス交渉には勢いが出ます。"
  },
  "Mars-Moon": {
    keywords: ["爆発的な熱意", "行動の衝動", "情緒的扇動"],
    text: "感情の昂ぶりがそのまま行動を駆動。周りを巻き込む強い影響力を持ちますが、大衆心理は動揺しやすく、相場の乱高下を引き起こす要因となります。"
  },
  "Mars-Neptune": {
    keywords: ["不確かな努力", "エネルギーの霧散", "弱さの露呈"],
    text: "意志の力が削がれやすく、目的を見失う懸念。理想を追いすぎて空回りするか、不正や不明瞭な活動による混乱が生じやすいので警戒が必要です。"
  },
  "Mars-NorthNode": {
    keywords: ["集団の突撃", "協力的な行動", "目標の共有"],
    text: "複数のエネルギーが一つの方向に向かう協力的な局面。物理的なプロジェクトの推進や、目的を同じくする団体による積極的な活動が目立ちます。"
  },
  "Mars-Pluto": {
    keywords: ["不屈の闘志", "極限の力", "宿命的な達成"],
    text: "凄まじいエネルギーによる目標への執着。不可能を可能にする力がありますが、独裁的な傾向や急進的な価格破壊など、極端な変動に注意。"
  },
  "Mars-Saturn": {
    keywords: ["忍耐強い闘争", "限界の打破", "強制の受容"],
    text: "「壁」にぶつかりながらも、必死の覚悟で進む困難な道。物理的な制限や停滞を乗り越えようとする強い圧力があり、事態が最終局面を迎えます。"
  },
  "Mars-Sun": {
    keywords: ["行動の具現化", "決定的な意志", "物理的な力"],
    text: "自己を強く打ち出し、具現化に向かうエネルギー。トップの決断能力が高まり、実行力を伴った計画が市場を牽引する力強いフェーズです。"
  },
  "Mars-Uranus": {
    keywords: ["急進的な変容", "突発的な変革", "衝撃"],
    text: "予測不能なエネルギーの介入。衝撃的なニュースや技術的なトラブルが既存の枠組みを壊し、突発的なトレンドの変化を強引に引き起こします。"
  },
  "Mars-Venus": {
    keywords: ["情熱的な表現", "創造の衝動", "魅力の発揮"],
    text: "愛情と意欲が合致し、魅力的なプレゼンテーションや創造的な作品が生まれるとき。消費活動も活発になり、華やかなトレンドが形成されます。"
  },

  // --- Mercury (水星) 絡み ---
  "Mercury-Moon": {
    keywords: ["感覚的な思考", "情報の機敏さ", "世論の変動"],
    text: "他者の心理を敏感に察知する洞察力。ニュースや噂が投資家心理にダイレクトに波及し、情報に対する即座の反応が相場を揺らします。"
  },
  "Mercury-Neptune": {
    keywords: ["直感的な発想", "不透明な言説", "幻想"],
    text: "想像力は豊かですが、事実関係が曖昧になりやすい時期。誇大広告や不明瞭な計画による混乱に注意し、本質を見極める必要があります。"
  },
  "Mercury-NorthNode": {
    keywords: ["情報のネットワーク", "交渉の活発化", "知識の共有"],
    text: "他者との意見交換が実を結び、ビジネスチャンスが広がる好機。対話を通じた合意形成や、新しい連絡網の構築が市場を活性化させます。"
  },
  "Mercury-Pluto": {
    keywords: ["徹底した調査", "説得の力", "プロパガンダ"],
    text: "深層の真実を掘り起こすような知的作業。強力な説得術が振るわれる一方で、偏った情報による操作も起きやすい、非常に神経質な時期です。"
  },
  "Mercury-Saturn": {
    keywords: ["論理の徹底", "慎重な分析", "現実的な見通し"],
    text: "思考に重みが増し、シビアな判断が下されるとき。ネガティブな指標には悲観的になりやすいですが、堅実なリスク管理には最適なフェーズです。"
  },
  "Mercury-Sun": {
    keywords: ["知的リーダーシップ", "公的伝達", "実務能力"],
    text: "明確な方向性を持ったビジネスの動き。公式なプレスリリースや計画の周知が順調に進み、商取引や通信分野で合理的な発展が見込めます。"
  },
  "Mercury-Uranus": {
    keywords: ["革新的なアイデア", "知的な飛躍", "驚きの発見"],
    text: "既存の考えを打ち破る斬新な着想。ITやハイテク技術の急進的な進歩を予感させ、想定外のニュースが知的な興奮を市場にもたらします。"
  },
  "Mercury-Venus": {
    keywords: ["洗練された対話", "外交的調和", "美的センス"],
    text: "優雅で誠実なコミュニケーションが事態を円滑にします。金銭面での有利な交渉や、デザイン性・機能性を兼ね備えた商品が注目されるときです。"
  },

  // --- Moon (月) 絡み ---
  "Moon-Neptune": {
    keywords: ["情緒の混濁", "感受性の高揚", "不安定な期待"],
    text: "論理よりも気分が優先される不透明な展開。漠然とした不安や、実体のない楽観に振り回されやすく、判断を誤りやすい繊細な時期です。"
  },
  "Moon-NorthNode": {
    keywords: ["大衆の連帯", "共鳴する感情", "集団心理"],
    text: "広く社会全体が共有するテーマへの関心。投資家コミュニティの熱狂や、大衆の日常的な感覚に寄り添ったトレンドが生まれやすい配置です。"
  },
  "Moon-Pluto": {
    keywords: ["極端な心理状態", "パニック", "深層の刷新"],
    text: "集団の感情が極限まで増幅されるとき。徹底的な変化を避けることができず、精神的な衝撃を伴うようなドラスティックな再編が起こります。"
  },
  "Moon-Saturn": {
    keywords: ["心理的抑圧", "不安の定着", "忍耐の必要性"],
    text: "意気消沈したムードが市場を覆う時期。情緒的な制限や家政・経済の冷え込みに敏感になり、防御的な姿勢が強まる調整のフェーズです。"
  },
  "Moon-Sun": {
    keywords: ["内面の一致", "調和的な関係", "均衡の達成"],
    text: "心身のバランスが整い、協力体制が円滑に機能します。公的な立場と大衆感情が一致し、安定した合意やトレンドが形成される象徴的配置。"
  },
  "Moon-Uranus": {
    keywords: ["感情の急変", "大衆の覚醒", "突発的な興奮"],
    text: "予期せぬ出来事に対する情緒的な動揺。一過性のパニックや熱狂が生じやすく、大衆の価値観が突如として変わるような場面に遭遇します。"
  },
  "Moon-Venus": {
    keywords: ["情緒的な充足", "人気の向上", "豊かな消費"],
    text: "幸せな気持ちが広まり、娯楽や装飾、美に関連する市場が活性化します。消費者の意欲が高まり、全体として好意的で平和な空気が漂います。"
  },

  // --- Neptune (海王星) 絡み ---
  "Neptune-NorthNode": {
    keywords: ["不透明な同盟", "幻想の共有", "背信の懸念"],
    text: "信頼関係に霧がかかる可能性。期待先行の実体のない提携や、甘い誘惑による集団的な判断ミスへの注意が必要な警戒すべき局面に。"
  },
  "Neptune-Pluto": {
    keywords: ["深層の激変", "秘匿された力", "大きな再構築"],
    text: "目に見えない大きな潮目が変わる時期。既存の価値観が根底から突き崩され、長期的な再編が始まる、不可逆な変化の予兆を象徴します。"
  },
  "Neptune-Saturn": {
    keywords: ["慢性的な脆弱性", "長期の低迷", "損失の暴露"],
    text: "不透明な経済状況が続き、じわじわと体力が削られるとき。隠されていた課題が露呈し、損失の確定や長期的な調整を余儀なくされます。"
  },
  "Neptune-Sun": {
    keywords: ["目的の不鮮明化", "活力の溶解", "弱体化"],
    text: "意志が散らばり、確固たる目標を失いやすい時期。スキャンダルや疑惑の浮上、物理的には資源に関連する不安が市場の重石となります。"
  },
  "Neptune-Uranus": {
    keywords: ["異常な興奮", "未知の体験", "神経の揺らぎ"],
    text: "常識の枠を超えた奇異な出来事への遭遇。過度な市場の熱狂や、未知の技術・理論による混乱など、極めて高いボラティリティが生じます。"
  },
  "Neptune-Venus": {
    keywords: ["美的憧憬", "判断の誤り", "期待過剰"],
    text: "美しい夢を描くが、現実を直視できていない配置。市場では一時的なバブルを生みますが、その後の幻滅や修正のリスクが伴う危うい時期です。"
  },

  // --- NorthNode (ノード) 絡み ---
  "NorthNode-Pluto": {
    keywords: ["宿命的な変容", "権力の再編", "集団の結束"],
    text: "社会全体を巻き込む強大な勢力の結集。権力構造が劇的に変化し、逃れられない運命的な潮流によって多くの組織が再編される節目です。"
  },
  "NorthNode-Saturn": {
    keywords: ["提携の解消", "連帯の阻害", "重い責任分担"],
    text: "人間関係や外交において、厳格な境界線が引かれるとき。不採算な協力関係の破棄や、孤独な判断を強いられる責任ある立場が強調されます。"
  },
  "NorthNode-Sun": {
    keywords: ["公式なパートナーシップ", "連帯の強化", "社交の発展"],
    text: "社会的な交流が活発化し、有意義な目的を持つ集団が形成されます。大規模な協力プロジェクトや、名声ある団体との接触において幸運があります。"
  },
  "NorthNode-Uranus": {
    keywords: ["突発的な合意", "改革勢力の誕生", "ハプニング"],
    text: "予期せぬタイミングでの新しい同盟や協力。技術的な革新を志向する人々との出会いがあり、突発的なニュースによって集団の計画が刷新されます。"
  },
  "NorthNode-Venus": {
    keywords: ["友好的な結合", "人気の獲得", "愛される提携"],
    text: "愛情と調和に満ちた社会的なつながり。レジャーやアート分野での成功を収める提携や、周囲から温かく受け入れられる平和的な進展です。"
  },

  // --- Pluto (冥王星) 絡み ---
  "Pluto-Saturn": {
    keywords: ["過酷な試練", "根底からの破壊", "忍耐の極致"],
    text: "「物理的・構造的な破壊と再生」。最悪の事態から這い上がるための徹底的な浄化。非常に重い圧迫感がありますが、再生のための必須の調整です。"
  },
  "Pluto-Sun": {
    keywords: ["徹底的な行使", "刷新のエネルギー", "宿命"],
    text: "強力なリーダーシップによる再構築。個人の力を超えた巨大な力が働き、既存のシステムの完全な刷新を促す、不可抗力的な力の展開です。"
  },
  "Pluto-Uranus": {
    keywords: ["時代の転換", "破壊的な革新", "パラダイムシフト"],
    text: "数十年単位のトレンドが突如として崩壊し、新しいパラダイムが誕生する激動期。技術や地政学における劇的な刷新が市場を根本から書き換えます。"
  },
  "Pluto-Venus": {
    keywords: ["極端な魅力", "価値の変化", "カリスマ的な表現"],
    text: "抗いがたい魅力や金銭的な野心。特定の市場が異常なまでに過熱するか、贅沢品の価値が根本から定義し直されるような極端な変動に陥ります。"
  },

  // --- Saturn (土星) 絡み ---
  "Saturn-Sun": {
    keywords: ["自制と節制", "責任の増大", "現実の直視"],
    text: "成長のペースを抑え、基盤を固める厳しい試練。慎重な経済政策が優先され、市場は上昇力が欠けるものの、実力のある部分だけが残る時期です。"
  },
  "Saturn-Uranus": {
    keywords: ["束縛と解放の対立", "突発的中断", "新旧の激突"],
    text: "保守的な守りと革新的な攻めの激しいぶつかり合い。突然の規約変更や体制の崩壊など、強い緊張感を伴う一過性の亀裂が生じやすくなります。"
  },
  "Saturn-Venus": {
    keywords: ["愛情の冷却", "予算の削減", "価値の固定"],
    text: "消費意欲が減退し、質素さや将来への備えが重視されるとき。華やかさは消えますが、長期的な視点での真の価値が守られる、落ち着いたフェーズです。"
  },

  // --- Sun (太陽) 絡み ---
  "Sun-Uranus": {
    keywords: ["革新の光", "突発的な刷新", "独立の意志"],
    text: "現状を打破する独自の生命力の展開。衝撃的な決断や革新的な技術の台頭によって、周囲の認識が一気に刷新される、興奮に満ちた場面です。"
  },
  "Sun-Venus": {
    keywords: ["洗練された活力", "調和した成功", "魅力の拡散"],
    text: "温かい人間関係と美的な充足感。市場には安心材料が増え、高級品や芸術に関連する分野で、洗練された成長がポジティブに受け入れられます。"
  },

  // --- Uranus (天王星) 絡み ---
  "Uranus-Venus": {
    keywords: ["気まぐれな変動", "突然の好意", "斬新な美意識"],
    text: "想定外の出費や為替の激しい変動。これまでの好みから一転した、風変わりで斬新なデザインやトレンドが突如として脚光を浴びる動きです。"
  }
};

// =============================================================================
// 2. ミッドポイント軸定義 (96軸)
// 軸の持つ根本的なテーマと解釈
// =============================================================================

export const MIDPOINT_AXES = {
  // --- 基本天体軸 ---
  "Moon/Sun": { name: "公私の調和の軸", meaning: "自己の本質と感情的な安定の統合。公的な役割と私的な充足のバランス、社会的な協力関係の形成。" },
  "Mercury/Sun": { name: "知的活動の軸", meaning: "論理的な思考プロセスと対外的な伝達。ビジネス上の意思決定、実務的な情報の整理、合理的な判断。" },
  "Sun/Venus": { name: "美的価値の軸", meaning: "生命力に美しさと調和をもたらすエネルギー。芸術的感性、社交的な魅力、経済的な豊かさへの愛着。" },
  "Mars/Sun": { name: "具現化力の軸", meaning: "目的達成に向けた直接的な行動と実行力。物理的な活力の投下、競争、事業を推進する力強い意志。" },
  "Jupiter/Sun": { name: "成功と発展の軸", meaning: "健全な成長と社会的な認知の拡大。楽観的な見通し、法的な優位性、全体的な繁栄の波に乗る動き。" },
  "Saturn/Sun": { name: "基盤安定の軸", meaning: "責任感と忍耐による構造の構築。一時的な制限を受け入れ、長期的な安定のために地盤を固める時期。" },
  "Sun/Uranus": { name: "革新と突破の軸", meaning: "現状を打破する独自のアイデアと閃き。突発的なニュースによる刷新、技術的な進歩、独立した行動。" },
  "Neptune/Sun": { name: "感性と直感の軸", meaning: "論理を超えた微細なエネルギーへの反応。不透明な状況下での直感力、期待先行、実体のなさへの警戒。" },
  "Pluto/Sun": { name: "再構築と権威の軸", meaning: "強烈な意志による根底からの変容。目標への執念、不可抗力な力による刷新、支配的なリーダーシップ。" },
  "NorthNode/Sun": { name: "提携と協力の軸", meaning: "目的を共有する人々との公的なつながり。有意義な協力体制の構築、社会的なコミュニティへの参加。" },
  "Chiron/Sun": { name: "存在意義の修復の軸", meaning: "アイデンティティに伴う過去の課題と再生。指導力の是正、自律的な復帰、独自の貢献。 " },

  "Mercury/Moon": { name: "情緒と情報の軸", meaning: "大衆感情と言説の融合。投資家心理を揺さぶる噂話、情報の機敏な察知、世論の動向把握。" },
  "Moon/Venus": { name: "情愛の充足の軸", meaning: "内面的な満足感と調和した魅力。生活の質へのこだわり、芸術的な充足、人気と経済的支援の獲得。" },
  "Mars/Moon": { name: "熱狂と扇動の軸", meaning: "感情的な衝動が高揚する配置。大衆の熱狂的な支持または動揺、即座の反応がもたらす激しい変動。" },
  "Jupiter/Moon": { name: "大衆支持と寛容の軸", meaning: "社会全体の心理的な高揚と受容。広範な人気、経済的な楽観、公私にわたる円満な発展。 " },
  "Moon/Saturn": { name: "心理적停滞の軸", meaning: "感情的な重圧と慎重な姿勢。大衆心理の冷え込み、制限された環境、耐乏による防衛的な動き。" },
  "Moon/Uranus": { name: "情緒的刷新の軸", meaning: "突然の出来事が引き起こす心理的な動揺。驚きを伴う価値観の変化、自立への興奮、環境の激変。" },
  "Moon/Neptune": { name: "敏感な想像力の軸", meaning: "境界が失われた過敏な状態。不透明な期待や失望、実体のない噂、集団的な幻惑への警戒。" },
  "Moon/Pluto": { name: "極端な感情変動の軸", meaning: "集団の深層心理が爆発的に動くとき。狂信的な反応、パニック、強制的な再編に伴う心理的衝撃。 " },
  "Moon/NorthNode": { name: "集団共鳴の軸", meaning: "広く大衆と共有される感情の流れ。社会的な連帯感、共通のテーマによる提携、集団的なトレンド。" },
  "Chiron/Moon": { name: "深層の癒やしの軸", meaning: "情緒の奥深くにある傷の解消。受容的なケア、内面の浄化プロセス、過去の否定的なパターンの克服。 " },

  "Mercury/Venus": { name: "洗練された表現の軸", meaning: "知性と美意識が合致した外交力。価値のある情報の洗練、有利な交渉、芸術的かつ商業的な成功。" },
  "Mars/Mercury": { name: "論理的決断の軸", meaning: "迅速な思考とそれを即座に実行する力。議論の先導、商取引の積極的な推進。性急な判断に注意。" },
  "Jupiter/Mercury": { name: "広範な思考の軸", meaning: "建設的な見通しと豊かな商才。有益な情報の拡散、教育・出版分野での成果、弁論による説得力。" },
  "Mercury/Saturn": { name: "徹底的分析の軸", meaning: "深い集中と論理の整合。シビアなリスク管理、現実的な見通し、情報の整理と長期的な思考。" },
  "Mercury/Uranus": { name: "洞察と飛躍の軸", meaning: "これまでの常識を超えた独創的な着想。IT・ハイテク技術への深い洞察、想定外の発見、知的な刷新。" },
  "Mercury/Neptune": { name: "直感と空想の軸", meaning: "境界のない思考と豊かな想像。不透明な言説の発生、誤報への注意、論理を超えたビジョンの模索。" },
  "Mercury/Pluto": { name: "知識と説得の軸", meaning: "真理を探究する徹底した調査と説得力。深層の情報の解明、強力な言論。強迫的な思考に注意。" },
  "Mercury/NorthNode": { name: "情報ネットワークの軸", meaning: "価値ある情報の共有とビジネス上の連携。意見の交換が富を生む好機、提携に向けた議論。" },
  "Chiron/Mercury": { name: "知的な気づきの軸", meaning: "思考プロセスの傷の修復。医療知識の深化、コミュニケーション課題の克服、教訓的な対話。" },

  "Mars/Venus": { name: "創造的情熱の軸", meaning: "行動力と美意識が統合されたエネルギー。創造的な活動の爆発、物質的な価値の享受、魅力の具現化。" },
  "Venus/Mars": { name: "創造的情熱の軸", meaning: "行動力と美意識が統合されたエネルギー。創造的な活動の爆発、物質的な価値の享受、魅力の具現化。" },
  "Jupiter/Venus": { name: "調和と利益の軸", meaning: "平和的な和解と経済的な成功の合致。社交の繁栄、愛着を感じる対象への富の集中、芸術の隆盛。" },
  "Venus/Jupiter": { name: "調和と利益の軸", meaning: "平和的な和解と経済的な成功の合致。社交の繁栄、愛着を感じる対象への富の集中、芸術の隆盛。" },
  "Saturn/Venus": { name: "価値の保存の軸", meaning: "愛情や金銭に対する規律ある姿勢。無駄の排除、義務に基づいた選択。一時的な冷え込みへの対処。" },
  "Uranus/Venus": { name: "美的興奮の軸", meaning: "突然の出会いや予期せぬトレンドの発生。突発的な出費、価値観の激変、斬新なデザインへの関心。" },
  "Venus/Uranus": { name: "美的興奮の軸", meaning: "「突然の熱狂とインスピレーション」。直感的な美的センス、一目惚れや劇的な出会い、投機的な高揚感、新しい流行の発生。" },
  "Neptune/Venus": { name: "高次元の美の軸", meaning: "崇高な理想を追う愛。現実離れした期待、美しい幻影、実体のない価値への陶酔。欺瞞への警戒。" },
  "Venus/Neptune": { name: "高次元の美の軸", meaning: "崇高な理想を追う愛。現実離れした期待、美しい幻影、実体のない価値への陶酔。欺瞞への警戒。" },
  "Pluto/Venus": { name: "魅力の変容の軸", meaning: "抗いがたい引力と価値観の抜本的な変化。カリスマ的な人気、特定銘柄への執着、莫大な富の再定義。" },
  "Venus/Pluto": { name: "魅力の変容の軸", meaning: "抗いがたい引力と価値観の抜本的な変化。カリスマ的な人気、特定銘柄への執着、莫大な富の再定義。" },
  "NorthNode/Venus": { name: "人気の結合の軸", meaning: "好意的なコミュニティとのつながり。愛される縁の誕生、平和な社交、有益なビジネス提携。" },
  "Chiron/Venus": { name: "価値認識の是正の軸", meaning: "愛の傷、金銭的なコンプレックス、美的バランスの再定義。 " },

  "Mars/Jupiter": { name: "意欲的展開の軸", meaning: "「成功への攻勢」。積極的なビジネス展開、事業の急成長、有利な法的判断、確信を持った実行。" },
  "Jupiter/Mars": { name: "成功の決断力の軸", meaning: "目標への最短距離を歩むエネルギー。投機的な好機への果敢な挑戦、建設的なプロジェクト、誕生と繁栄。" },
  "Mars/Saturn": { name: "停滞と葛藤の軸", meaning: "「破壊と忍耐」。強大なエネルギーへの抑圧、不屈の努力を要する困難、物理的な損失や構造的な障害。" },
  "Mars/Uranus": { name: "突発的な実行の軸", meaning: "「急変と介入」。想定外の事態への即応、刷新のための強硬策。事故や障害、急激な方向転換への警戒。" },
  "Mars/Neptune": { name: "意志の散漫の軸", meaning: "「失敗と混乱」。計画の不備、実体のない期待による損失、エネルギーの漏洩。不祥事や隠れた問題に注意。" },
  "Mars/Pluto": { name: "破壊的エネルギーの軸", meaning: "「限界突破」。強大な力による支配、抜本的な構造の破壊と再生。非情なまでの効率追求、激しい変動。" },
  "Mars/NorthNode": { name: "共同戦線の軸", meaning: "目的を共にする者との一致団結。組織的な協力、共同での大規模プロジェクト、集団的な熱狂の共有。" },
  "Chiron/Mars": { name: "闘志の再生の軸", meaning: "傷ついた実行力の回復。攻撃性の健全な昇華、専門的な技術による解決、守備的な戦い。 " },

  "Jupiter/Saturn": { name: "社会的均衡の軸", meaning: "「拡大と縮小の循環」。景気サイクルの転換点、組織の代謝、忍耐を伴う安定成長、法的な再編。" },
  "Jupiter/Uranus": { name: "好機的転換の軸", meaning: "「サプライズな改善」。突発的な幸運、規制の緩和、独創的な発明、楽観的な市場心理、解放感。" },
  "Jupiter/Neptune": { name: "楽観と幻惑の軸", meaning: "根拠のない期待、バブル的な膨張、高次元のビジョン。不確かな情報や過剰な投機への警告。" },
  "Jupiter/Pluto": { name: "最大発展の軸", meaning: "圧倒的な成功と支配権の獲得。巨額の利益、カリスマ的指導力、目標達成への強烈なコミットメント。" },
  "Jupiter/NorthNode": { name: "有益な提携の軸", meaning: "社会的な成功をもたらす良好な縁。Win-Winの関係構築、提携による利益の拡大、幸運な協力者の出現。" },
  "Chiron/Jupiter": { name: "導きの叡智の軸", meaning: "高度な専門知識による救済。法的な修復、教育的な指針、精神的な豊かさをもたらすメンター。" },

  "Saturn/Uranus": { name: "構造変革の軸", meaning: "「新旧の激突」。伝統と革新の対立、強引な規律の導入、緊張を伴う構造改革、突然の離別。" },
  "Neptune/Saturn": { name: "衰退と忍耐の軸", meaning: "「慢性的な弱体化」。不透明な不安、秘密の敵の出現、抑圧された精神状態。長期的な調整過程。" },
  "Pluto/Saturn": { name: "抜本的再建の軸", meaning: "「厳しい試練」。根底からの破壊、過酷な状況下での再編、最小単位からの再出発、重大な損失。" },
  "NorthNode/Saturn": { name: "孤立と責任の軸", meaning: "協力関係の解消、外交上の冷却。一人で背負う重責、冷え込んだ人間関係、物理的な制約。" },
  "Chiron/Saturn": { name: "権威の克服の軸", meaning: "構造的な不備の是正。責任感の重圧からの解放、父権的な課題の解消、長期的なプロセスの癒やし。" },

  "Neptune/Uranus": { name: "深層の目醒めの軸", meaning: "潜在意識下での変化、超自然的な現象、神経の昂ぶり、常識を超えた事態。新しい時代の予感。" },
  "Pluto/Uranus": { name: "大変革の軸", meaning: "「革命的な刷新」。既存システムの崩壊と根底からの再構築、技術の大転換。突然のパラダイムシフト。" },
  "NorthNode/Uranus": { name: "突発的交流の軸", meaning: "予期せぬ出会いと閃きの共有。一時的な興奮、共通の驚き、ハプニング的なネットワーキング。" },
  "Chiron/Uranus": { name: "技術的解決の軸", meaning: "独自の着眼点による突破。突発的な気づき、先端技術を用いた癒やし、過去への新たな視点。 " },

  "Neptune/Pluto": { name: "精神的刷新の軸", meaning: "深層心理の変容、秘密の露呈。長い時間をかけて判明する根本的な変動、神秘的なビジョンの浸透。" },
  "Neptune/NorthNode": { name: "不透明な連帯の軸", meaning: "偽りの期待、騙し、失望を伴う関係。誤解に基づいた提携。精神的な深いレベルでの共鳴。" },
  "Chiron/Neptune": { name: "自己犠牲の軸", meaning: "献身と救済のプロセス。被害者意識の克服、精神的な癒やし、実体のない不安の浄化。" },

  "NorthNode/Pluto": { name: "宿命的結合の軸", meaning: "強力な影響力を持つ集団との縁。運命的な組織変革、大衆との強固な結びつき、支配的な提携。" },
  "Chiron/Pluto": { name: "深層心理の浄化の軸", meaning: "魂の暗部への対峙。根本的な病弊の除去、破壊を伴う再編、極限状態での再生プロセス。" },

  "Chiron/NorthNode": { name: "縁の修復の軸", meaning: "集団の中での役割の再定義。魂の目的と一致する出会い、カルマの解消を伴う協力関係。" },

  // --- ASC (アセンダント) 軸 ---
  "Sun/ASC": { name: "自己認識と環境の軸", meaning: "周囲への公的な関わり。重要な出会い、環境への自己投影、身体的な反応が促される時。" },
  "Moon/ASC": { name: "環境への同調の軸", meaning: "他者や周囲との感情的な交流。強い適応力、大衆心理への感応、女性に関連する関心の高まり。" },
  "Mercury/ASC": { name: "対話の深化の軸", meaning: "情報の活発な交換。意見の衝突や合致、仲介・交渉の機会、身の回りのニュース。" },
  "Venus/ASC": { name: "社交的調和の軸", meaning: "周囲に良い印象を与えるエネルギー。平和的な交流、魅力の開花、有利な条件での契約、人気。" },
  "Mars/ASC": { name: "共同作業の軸", meaning: "協力または対立する力。闘争的な環境、積極的なアプローチ、共同での実行、物理的な興奮。" },
  "Jupiter/ASC": { name: "社交的成功の軸", meaning: "周囲からの援助と幸運な出会い。調和のとれた対人関係、ビジネスの拡大、楽観的な環境、結婚。 " },
  "Saturn/ASC": { name: "境界と孤立の軸", meaning: "他者との距離、制約を感じる関係。孤独な決断、厳しい環境、別離、長期的な信頼の構築。 " },
  "Uranus/ASC": { name: "環境の刷新の軸", meaning: "予期せぬ場所変えや環境の激変。突然の出会い、興奮、落ち着かない周囲の状況、サプライズ。" },
  "Neptune/ASC": { name: "不透明な関係の軸", meaning: "周囲への不信感、欺瞞、誤解。過敏な反応、弱い立場への同情、隠された真実への違和感。" },
  "Pluto/ASC": { name: "運命的衝撃の軸", meaning: "他者からの強烈な影響と変容。支配的な関係、強制的な再編、圧倒的な外部要因による変化。" },
  "NorthNode/ASC": { name: "共同体形成の軸", meaning: "同じ目的を持つ人々との絆。チームワーク、家族的な結合、組織的な団結。 " },
  "ASC/MC": { name: "個性の発揮の軸", meaning: "公私のバランスと自己表現。環境の中での自分の立ち位置を確認し、目的を具体化させる段階。" },
  "Chiron/ASC": { name: "独自の指導の軸", meaning: "他者の痛みを理解する対人関係。医療や占星術関係者との縁、メンタルケア、トラウマの共有。" },
  "Aries/ASC": { name: "邂逅の軸", meaning: "公の場所での自分と他者の関わり。特定の環境における自己の表れ、世界との接点。" },

  // --- MC (ミッドヘブン) 軸 ---
  "Sun/MC": { name: "人生の目的の軸", meaning: "「自己の実現」。名誉、キャリア、公的な成功、意志の決定。目標に向かう明確な意識。" },
  "Moon/MC": { name: "情緒と目標の軸", meaning: "魂の充実を伴う職業。大衆の人気、女性的な視点、感情的な満足、天職への意識。" },
  "Mercury/MC": { name: "職業的思考の軸", meaning: "自身の能力に対する知的なアプローチ。計画の立案、評価、情報を通じた自己表現、認識の確立。" },
  "Venus/MC": { name: "美的充足の軸", meaning: "調和のとれたキャリア。芸術的評価、愛情、社交的な成功、人気銘柄への関心、心地よい達成。" },
  "Mars/MC": { name: "意思貫徹の軸", meaning: "「成功への執念」。積極的な行動、昇進、目的達成。意志の衝突があるにせよ成果を掴む力。" },
  "Jupiter/MC": { name: "最高の成功の軸", meaning: "「社会的繁栄」。名声、巨富、地位の向上、全体的な満足。幸運なキャリアの進展。" },
  "Saturn/MC": { name: "責任の完遂の軸", meaning: "「重責と忍耐」。孤独な闘い、地位の喪失または固守、物理的な重圧。長期的な安定への試練。" },
  "Uranus/MC": { name: "キャリア改革の軸", meaning: "突然の地位変更、独立、革新的な技術の採用。緊張感を伴う刷新、型にハマらない目標設定。" },
  "Neptune/MC": { name: "ビジョンと幻惑の軸", meaning: "不透明な目的意識。理想主義、精神的な仕事。直感に導かれる成功、あるいは目標の喪失、失望。" },
  "Pluto/MC": { name: "究極の野心の軸", meaning: "「絶対的な力」。破壊と再生を伴う飛躍、極端な指導権の行使。圧倒的な影響力の実使。" },
  "NorthNode/MC": { name: "職業的提携の軸", meaning: "目標を共有する協力者。有益なコネクション、ビジネスパートナーシップ、共同の成果。" },
  "Chiron/MC": { name: "独自の貢献の軸", meaning: "痛みを知る者としての指導力。癒やしの仕事への従事、社会復帰への強い意志、導き手としての職責。" },
  "Aries/MC": { name: "自我の確信の軸", meaning: "「現在の自己」。世界の中での独自の立ち位置の確認。自分自身であることを自覚する意識。" },

  // --- Aries Point (牡羊座0度) 軸 ---
  "Sun/Aries": { name: "公的認識の軸", meaning: "「世界の中の個」。社会的な認知、公人としての活動、広く社会全体に及ぼす影響。" },
  "Moon/Aries": { name: "大衆心理の軸", meaning: "「集団の潜在意識」。社会全体の感情動向、市民生活、一般的な流行や不特定多数の動向。" },
  "Mercury/Aries": { name: "世論とニュースの軸", meaning: "「公衆の思考」。情報の拡散、一般的な知識、社会的な議論、交通・通信・物流への影響。" },
  "Venus/Aries": { name: "社会的美意識の軸", meaning: "「社交とトレンド」。公的な魅力、社会全体で共有される美学、経済的な盛り上がり、大衆の人気。" },
  "Mars/Aries": { name: "社会活動の軸", meaning: "「実行される意志」。公的な活動、集団的な企て、社会全体の活力、闘争精神の発露、物理的変動。" },
  "Jupiter/Aries": { name: "一般的繁栄の軸", meaning: "「幸福な時流」。社会全体の好機、全体的な拡大、幸福感に満ちた社会情勢、経済の繁栄。" },
  "Saturn/Aries": { name: "公的制約の軸", meaning: "「社会の冷え込み」。一般的な制限、物理的な遮断、困難な社会情勢、義務感による停滞。" },
  "Uranus/Aries": { name: "社会的大改革の軸", meaning: "「時代を動かす突発事」。世界的なサプライズ、技術の劇的な進歩、急進的な社会変動。" },
  "Neptune/Aries": { name: "社会的幻惑の軸", meaning: "「不透明な混乱」。社会全体の不安、流行病の拡大、事実に基づかない言説、神秘的な風潮。" },
  "Pluto/Aries": { name: "世界的大変容の軸", meaning: "「不可抗力な力」。根底からの社会の刷新、強制的な構造の変化、巨大な権威の行使、歴史の転換。" },
  "NorthNode/Aries": { name: "広域的連帯の軸", meaning: "「共同体の意識」。国際的な同盟、社会的なつながりの強化、大規模なネットワークの形成。" },
  "Chiron/Aries": { name: "社会的な痛みの軸", meaning: "「公衆の傷」。広範囲な救済活動、社会全体のトラウマの癒やし、医療体制の変革。" },
};

// =============================================================================
// 3. トリガー惑星の作用
// =============================================================================

export const TRIGGER_PLANETS = {
  Sun: {
    action: "がトレンドを鮮明化する",
    effect: "【市場の焦点】 軸のテーマに注目が集まり、トレンドが顕在化します。活発な取引と方向性の確立。"
  },
  Moon: {
    action: "が大衆心理を揺さぶる",
    effect: "【センチメントの変化】 投資家の感情が変動しやすく、短期間のボラティリティや気迷いが生じます。"
  },
  Mercury: {
    action: "が情報を拡散させる",
    effect: "【ニュース材料】 重要ニュースや統計、噂が飛び交い、実務的な取引や議論が活発になります。"
  },
  Venus: {
    action: "が価値を再評価させる",
    effect: "【魅力の向上】 投資対象としての魅力が高まり、買い意欲の増進や円満な合意、利益の確定が促されます。"
  },
  Mars: {
    action: "が勢いを着火する",
    effect: "【モメンタム】 強気または弱気の勢いが急増し、直接的な売買行動へ。急変やトラブルの引き金にも。"
  },
  Jupiter: {
    action: "を大きく拡張させる",
    effect: "【市場の拡大】 楽観的な見通しが広がり、資産価値の増大や新たな投資機会、成功への確信が強まります。"
  },
  Saturn: {
    action: "に抑制をかける",
    effect: "【抵抗と制限】 上値の重さ、流動性の低下、あるいは現実的なリスク管理の必要性が生じ、慎重さが求められます。"
  },
  Uranus: {
    action: "を劇的に刷新する",
    effect: "【サプライズ】 一転しての急変、テクノロジーによる革新、常識を覆すニュースによる激しい変動。"
  },
  Neptune: {
    action: "を不透明にする",
    effect: "【幻惑と混迷】 根拠のない期待、バブル的な膨張、あるいは実体のない失望によって方向性を見失います。"
  },
  Pluto: {
    action: "を根本から再編する",
    effect: "【不可抗力な変容】 抜本的な構造変化、強制的な決済、巨大な権威による支配。死と再生を伴う激変。"
  },
  Aries: {
    action: "を社会へ波及させる",
    effect: "【広域化】 特定の事象が社会全体や市場全体のテーマへと具体化し、広く認知される段階。"
  },
  ASC: {
    action: "を現在地に現す",
    effect: "【環境への反映】 軸のテーマが身近な市場環境や特定の対人関係、取引の場を通じて具体化します。"
  },
  MC: {
    action: "を目標へと導く",
    effect: "【達成と自覚】 個人的な目的や魂の充足、あるいは社会的な野心として軸の現象を強く体験します。"
  }
};